import React from "react";
import NoDataComponent from "./noData";
import { FullscreenOutlined } from "@ant-design/icons";
import { Button, Tooltip } from "antd";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import PaginationComponent from './pagination';

function CreativeTableContainer(props) {
  const { data, loading, currentPage, totalPage, handleChangePage } = props;

  const containerRef = React.useRef(null);

  const handleToggleFullscreen = React.useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      if (el.requestFullscreen) el.requestFullscreen();
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  }, []);

  return (
    <>
      <div className="d-flex justify-content-end mb-2">
        <div>
          <Tooltip title="Full Screen">
            <Button type="dashed" icon={<FullscreenOutlined />} onClick={handleToggleFullscreen} />
          </Tooltip>
        </div>
      </div>
      <div className="table-container d-flex" ref={containerRef}>
        <div className="table-scroll-wrapper">
          <table
            className="table table-bordered table-hover tb-campaign-center"
            style={{ tableLayout: "fixed" }}
          >
            <thead className="thead-light table-fixed-header">
              <tr key="t-head1">
                <th
                  className="sticky-col-left th-text-filter"
                  style={{ width: "300px", height: "50px", verticalAlign: "middle" }}
                >
                  Ad/Asset
                </th>
                <th
                  className="text-center"
                  style={{ width: "200px", height: "50px", verticalAlign: "middle" }}
                >
                  Impressions
                </th>
                <th
                  className="text-center"
                  style={{ width: "200px", height: "50px", verticalAlign: "middle" }}
                >
                  IPM
                </th>
                <th
                  className="text-center"
                  style={{ width: "200px", height: "50px", verticalAlign: "middle" }}
                >
                  CTR
                </th>
                <th
                  className="text-center"
                  style={{ width: "200px", height: "50px", verticalAlign: "middle" }}
                >
                  CTI
                </th>
                <th
                  className="text-center"
                  style={{ width: "120px", height: "50px", verticalAlign: "middle" }}
                >
                  Percentage
                </th>
                <th
                  className="text-center"
                  style={{ width: "150px", height: "50px", verticalAlign: "middle" }}
                >
                  Last active
                </th>
              </tr>
            </thead>
            <tbody>
              {data.length > 0 ? (
                data.map((item, index) => (
                  <tr key={index}>
                    <td className="sticky-col-left" style={{ verticalAlign: "middle" }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                          {item?.name}
                        </span>
                        {item?.network && props.networkIcons && props.networkIcons[item.network] && (
                          <img 
                            width="20px" 
                            height="20px" 
                            src={`/icon/${props.networkIcons[item.network]}`}
                            alt={item.network}
                            style={{ marginLeft: '8px', borderRadius: "8px", flexShrink: 0 }}
                          />
                        )}
                      </div>
                    </td>
                    <td style={{ verticalAlign: "middle" }}>
                      <div style={{ width: '200px', height: '50px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={item.chartData}>
                            <RechartsTooltip 
                              content={({ active, payload, label }) => {
                                if (active && payload && payload.length) {
                                  const data = payload[0].payload;
                                  return (
                                    <div style={{ 
                                      background: 'white', 
                                      border: '1px solid #ccc', 
                                      borderRadius: '4px', 
                                      padding: '8px',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}>
                                      <p style={{ margin: 0, fontWeight: 'bold' , color: '#42a5f5' }}>{data.dateKey}</p>
                                      <p style={{ margin: 0, color: '#42a5f5' }}>
                                        Impressions: {payload[0].value?.toLocaleString()}
                                      </p>
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                            <Bar dataKey="impressions" fill="#42a5f5" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </td>
                    <td style={{ verticalAlign: "middle" }}>
                      <div style={{ width: '200px', height: '50px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={item.chartData}>
                            <RechartsTooltip 
                              content={({ active, payload, label }) => {
                                if (active && payload && payload.length) {
                                  const data = payload[0].payload;
                                  return (
                                    <div style={{ 
                                      background: 'white', 
                                      border: '1px solid #ccc', 
                                      borderRadius: '4px', 
                                      padding: '8px',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}>
                                      <p style={{ margin: 0, fontWeight: 'bold' , color: '#66bb6a' }}>{data.dateKey}</p>
                                      <p style={{ margin: 0, color: '#66bb6a' }}>
                                        IPM: {payload[0].value?.toLocaleString()}
                                      </p>
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                            <Bar dataKey="ipm" fill="#66bb6a" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </td>
                    <td style={{ verticalAlign: "middle" }}>
                      <div style={{ width: '200px', height: '50px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={item.chartData}>
                            <RechartsTooltip 
                              content={({ active, payload, label }) => {
                                if (active && payload && payload.length) {
                                  const data = payload[0].payload;
                                  return (
                                    <div style={{ 
                                      background: 'white', 
                                      border: '1px solid #ccc', 
                                      borderRadius: '4px', 
                                      padding: '8px',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}>
                                      <p style={{ margin: 0, fontWeight: 'bold' , color: '#ff7043' }}>{data.dateKey}</p>
                                      <p style={{ margin: 0, color: '#ff7043' }}>
                                        CTR: {payload[0].value?.toLocaleString()}
                                      </p>
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                            <Bar dataKey="ctr" fill="#ff7043" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </td>
                    <td style={{ verticalAlign: "middle" }}>
                      <div style={{ width: '200px', height: '50px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={item.chartData}>
                            <RechartsTooltip 
                              content={({ active, payload, label }) => {
                                if (active && payload && payload.length) {
                                  const data = payload[0].payload;
                                  return (
                                    <div style={{ 
                                      background: 'white', 
                                      border: '1px solid #ccc', 
                                      borderRadius: '4px', 
                                      padding: '8px',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}>
                                      <p style={{ margin: 0, fontWeight: 'bold', color: '#ab47bc' }}>{data.dateKey}</p>
                                      <p style={{ margin: 0, color: '#ab47bc' }}>
                                        CTI: {payload[0].value?.toLocaleString()}
                                      </p>
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                            <Bar dataKey="cti" fill="#ab47bc" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </td>
                    <td className="text-center" style={{ verticalAlign: "middle" }}>
                      {item.percentage === 0 ? '0.00' : item.percentage}%
                    </td>
                    <td className="text-center" style={{ verticalAlign: "middle" }}>
                      {item.last_active}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center">
                    <NoDataComponent />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {currentPage && totalPage && handleChangePage && (
        <div className="mt-3">
          <PaginationComponent
            current={currentPage}
            total={totalPage}
            handleChangePage={handleChangePage}
          />
        </div>
      )}
    </>
  );
}

export default CreativeTableContainer;
